import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  resolvedCount: null,
  resolvedPersonalCount: null,
  aceleresBoxCounts: null,
  aceleresBoxUnreadCounts: null,
  metaKey: null,
};

let metaRequestSequence = 0;
let lastAuthoritativeMetaAt = 0;
const inflightMetaRequests = new Map();
// v3 invalidates snapshots created before the unread-box correction. Keeping
// the old key made the Novos badge reappear as 1 until the browser cache TTL
// expired, even though /conversations/meta already returned zero.
const STATS_CACHE_PREFIX = 'zoraxy:conversation-stats:v4';
const STATS_CACHE_TTL = 2 * 60 * 1000;

const buildMetaKey = (params = {}) => {
  const labels = Array.isArray(params.labels) ? [...params.labels].sort() : [];

  return JSON.stringify({
    inboxId: params.inboxId || '',
    status: params.status || '',
    teamId: params.teamId || '',
    conversationType: params.conversationType || '',
    labels,
    priority: params.priority || '',
    assigneeId: params.assigneeId || '',
    atendidoIa: params.atendidoIa || '',
    aceleresBox: params.aceleresBox || params.aceleres_box || '',
    assigneeType: params.assigneeType || params.assignee_type || '',
  });
};

export const getters = {
  getStats: $state => $state,
};

const getStatsCacheKey = (metaKey, rootGetters = {}) => {
  const accountId = rootGetters.getCurrentAccountId;
  const userId = rootGetters.getCurrentUser?.id;
  if (!accountId || !userId || typeof window === 'undefined') return null;

  return `${STATS_CACHE_PREFIX}:${accountId}:${userId}:${metaKey}`;
};

const readCachedStats = cacheKey => {
  if (!cacheKey) return null;

  try {
    const cached = JSON.parse(window.sessionStorage.getItem(cacheKey));
    if (
      !cached?.meta ||
      !cached.savedAt ||
      Date.now() - cached.savedAt > STATS_CACHE_TTL
    ) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }
    return cached.meta;
  } catch {
    return null;
  }
};

const cacheStats = (cacheKey, meta) => {
  if (!cacheKey) return;

  try {
    window.sessionStorage.setItem(
      cacheKey,
      JSON.stringify({ meta, savedAt: Date.now() })
    );
  } catch {
    // A atualizacao de rede continua funcionando quando o storage nao existe.
  }
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params, requestId, metaKey, cacheKey) => {
  try {
    const response = await ConversationApi.meta(params);
    if (requestId !== metaRequestSequence) {
      return;
    }
    const {
      data: { meta },
    } = response;
    cacheStats(cacheKey, meta);
    commit(types.SET_CONV_TAB_META, { ...meta, metaKey });
  } catch (error) {
    // ignore
  }
};

const requestMetaData = (
  commit,
  params,
  requestId,
  metaKey,
  cacheKey,
  { immediate = false } = {}
) => {
  const pending = inflightMetaRequests.get(metaKey);
  if (pending && (pending.immediate || !immediate)) {
    return pending.promise;
  }

  const promise = fetchMetaData(
    commit,
    params,
    requestId,
    metaKey,
    cacheKey
  ).finally(() => {
    if (inflightMetaRequests.get(metaKey)?.promise === promise) {
      inflightMetaRequests.delete(metaKey);
    }
  });

  inflightMetaRequests.set(metaKey, { immediate, promise });
  return promise;
};

// Estas janelas sao o atraso entre "chegou/foi lida uma mensagem" e o numero
// novo aparecer/sumir no badge da aba e no icone Conversas. O ajuste otimista
// ja mexeu no contador local; isto e a reconciliacao com o servidor, que usa o
// cache de /conversations/meta (0,2s) no caminho normal -- por isso da pra
// encurtar sem multiplicar recalculo. Contas grandes seguem com folga maior.
const debouncedFetchMetaData = debounce(requestMetaData, 250, false, 800);
const longDebouncedFetchMetaData = debounce(requestMetaData, 600, false, 1500);
const superLongDebouncedFetchMetaData = debounce(
  requestMetaData,
  1500,
  false,
  4000
);

export const actions = {
  adjustUnreadCounts({ commit }, { box, delta } = {}) {
    if (!box || !Number.isFinite(Number(delta)) || Number(delta) === 0) return;

    commit(types.ADJUST_CONV_TAB_UNREAD_COUNTS, {
      box,
      delta: Number(delta),
    });
  },

  // Os contadores continuam globais enquanto a caixa Pessoais está selecionada.
  get: async ({ commit, state: $state, rootGetters }, params = {}) => {
    const metaParams = { ...params };
    const shouldFetchImmediately = Boolean(metaParams.immediate);
    delete metaParams.immediate;
    // Leitura/reabertura precisa refletir o estado persistido agora. O cache
    // HTTP do endpoint /conversations/meta pode ainda conter o contador antigo
    // e ressuscitar o badge depois do ajuste otimista da tela.
    if (shouldFetchImmediately) {
      lastAuthoritativeMetaAt = Date.now();
    }
    if (
      shouldFetchImmediately ||
      Date.now() - lastAuthoritativeMetaAt < 30 * 1000
    ) {
      metaParams.skip_cache = true;
    }
    const metaKey = buildMetaKey(metaParams);
    const pendingMetaRequest = inflightMetaRequests.get(metaKey);
    if (pendingMetaRequest?.immediate && !shouldFetchImmediately) {
      return pendingMetaRequest.promise;
    }

    const requestId = metaRequestSequence + 1;
    metaRequestSequence = requestId;
    const cacheKey = getStatsCacheKey(metaKey, rootGetters);
    const cachedStats = readCachedStats(cacheKey);
    // Uma atualização imediata deve refletir a resposta atual do servidor.
    // Reaplicar o snapshot da sessionStorage antes da rede fazia o contador
    // permanecer incorreto mesmo depois de a lista ser recarregada.
    if (cachedStats && !shouldFetchImmediately) {
      commit(types.SET_CONV_TAB_META, { ...cachedStats, metaKey });
    }
    if (shouldFetchImmediately) {
      return requestMetaData(commit, metaParams, requestId, metaKey, cacheKey, {
        immediate: true,
      });
    }

    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(
        commit,
        metaParams,
        requestId,
        metaKey,
        cacheKey
      );
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(
        commit,
        metaParams,
        requestId,
        metaKey,
        cacheKey
      );
    } else {
      debouncedFetchMetaData(commit, metaParams, requestId, metaKey, cacheKey);
    }

    return undefined;
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
};

export const mutations = {
  [types.ADJUST_CONV_TAB_UNREAD_COUNTS]($state, { box, delta } = {}) {
    const currentCounts = $state.aceleresBoxUnreadCounts || {};
    const currentValue = Number(currentCounts[box] || 0);

    $state.aceleresBoxUnreadCounts = {
      ...currentCounts,
      [box]: Math.max(0, currentValue + Number(delta || 0)),
    };
    $state.updatedOn = new Date();
  },

  // Aceleres: so sobrescreve cada contador quando o meta traz o campo como
  // numero. O endpoint /conversations/filter (filtro modal/IA) retorna meta
  // SEM mine_count/unassigned_count/all_count — sobrescrever com undefined
  // zerava as abas ate o proximo refresh periodico.
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: snakeMineCount,
      mineCount: camelMineCount,
      unassigned_count: snakeUnAssignedCount,
      unAssignedCount: camelUnAssignedCount,
      all_count: snakeAllCount,
      allCount: camelAllCount,
      resolved_count: snakeResolvedCount,
      resolvedCount: camelResolvedCount,
      resolved_personal_count: snakeResolvedPersonalCount,
      resolvedPersonalCount: camelResolvedPersonalCount,
      aceleres_box_counts: snakeAceleresBoxCounts,
      aceleresBoxCounts: camelAceleresBoxCounts,
      aceleres_box_unread_counts: snakeAceleresBoxUnreadCounts,
      aceleresBoxUnreadCounts: camelAceleresBoxUnreadCounts,
      meta_key: snakeMetaKey,
      metaKey: camelMetaKey,
    } = {}
  ) {
    const mineCount = snakeMineCount ?? camelMineCount;
    const unAssignedCount = snakeUnAssignedCount ?? camelUnAssignedCount;
    const allCount = snakeAllCount ?? camelAllCount;
    const resolvedCount = snakeResolvedCount ?? camelResolvedCount;
    const resolvedPersonalCount =
      snakeResolvedPersonalCount ?? camelResolvedPersonalCount;
    const aceleresBoxCounts = snakeAceleresBoxCounts ?? camelAceleresBoxCounts;
    const aceleresBoxUnreadCounts =
      snakeAceleresBoxUnreadCounts ?? camelAceleresBoxUnreadCounts;
    const metaKey = snakeMetaKey ?? camelMetaKey;

    if (typeof mineCount === 'number') $state.mineCount = mineCount;
    if (typeof allCount === 'number') $state.allCount = allCount;
    if (typeof resolvedCount === 'number') $state.resolvedCount = resolvedCount;
    if (typeof resolvedPersonalCount === 'number') {
      $state.resolvedPersonalCount = resolvedPersonalCount;
    }
    if (typeof unAssignedCount === 'number')
      $state.unAssignedCount = unAssignedCount;
    // Aceleres 4 caixas: {novos, meus, outros, pessoais} vindos do /meta.
    if (aceleresBoxCounts && typeof aceleresBoxCounts === 'object') {
      $state.aceleresBoxCounts = aceleresBoxCounts;
    }
    if (
      aceleresBoxUnreadCounts &&
      typeof aceleresBoxUnreadCounts === 'object'
    ) {
      $state.aceleresBoxUnreadCounts = aceleresBoxUnreadCounts;
    }
    if (typeof metaKey === 'string') $state.metaKey = metaKey;
    $state.updatedOn = new Date();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
